import React, { FC, MouseEvent } from 'react';

import { PrimaryMarketProject, ProjectGroupInfo, StatusKey } from 'api-client';
import { DataTable, MetricProgressBar, Money } from 'components/common';
import { ChevronIcon } from 'components/common/icons';
import { formatPercents } from 'formatters';
import { useToggle } from 'hooks';
import { cn, declOfNum } from 'utils';

import { buildGroupSegments, groupSizeLabel } from '../../groupSegments';
import { PrimaryMarketTableRow } from '../PrimaryMarketTableRow';
import styles from './PrimaryMarketGroupRow.module.scss';

interface Props {
  group: ProjectGroupInfo;
  members: Array<PrimaryMarketProject>;
  statusKey: StatusKey;
}

/**
 * Группа проектов в списке: одна сделка, разбитая на несколько проектов.
 *
 * Строка группы — не строка проекта, а заголовок блока, поэтому она не притворяется
 * проектом: счётчик рядом с названием называет её природу, полоса сбора разбита по
 * проектам (ширина сегмента — доля проекта в сделке, заливка — сколько по нему собрано),
 * а раскрытые проекты уходят на утопленную поверхность с отступом, чтобы вложенность
 * читалась и после прокрутки.
 *
 * Ячеек ровно столько же, сколько в PrimaryMarketTableRow, и обёртка не добавляет
 * горизонтальных отступов — иначе колонки разъехались бы с шапкой таблицы.
 */
const PrimaryMarketGroupRow: FC<Props> = ({ group, members, statusKey }) => {
  const [expanded, toggleExpanded] = useToggle();
  const isPaidTable = statusKey === 'Завершен';
  const membersId = `project-group-${group.uuid}`;

  // Вся строка кликабельна ради крупной цели для мыши, но раскрывает её настоящая кнопка:
  // role="button" на строке сделал бы ярлыком кнопки всё её содержимое, и суммы по сделке
  // превратились бы в длинное имя контрола вместо данных.
  const handleDisclosureClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    toggleExpanded();
  };

  return (
    <div className={cn(styles.group, expanded && styles.groupExpanded)}>
      <DataTable.Row className={styles.row} onClick={toggleExpanded}>
        <DataTable.Cell>
          <div className={styles.name}>{group.name}</div>
          <div className={styles.count}>{groupSizeLabel(group.memberCount)}</div>
        </DataTable.Cell>
        <DataTable.Cell className={styles.nowrap}>
          <Money money={group.totalDebtPrice} compact />
        </DataTable.Cell>
        <DataTable.Cell className={styles.nowrap}>
          <Money money={group.totalRecoveryForecast} compact />
        </DataTable.Cell>
        {/* Средневзвешенные по сумме сбора — знак приближения, чтобы их не читали как точные. */}
        <DataTable.Cell className={styles.nowrap}>
          {`≈ ${formatPercents(group.weightedSimpleInterestRate, 1, true)} / ${formatPercents(
            group.weightedInterestRate,
            1,
            true
          )}`}
        </DataTable.Cell>
        <DataTable.Cell className={styles.nowrap}>
          {`≈ ${group.weightedInitialTerm.toFixed(0)} мес.`}
        </DataTable.Cell>
        <DataTable.Cell>
          <MetricProgressBar
            currentValue={<Money money={group.totalInvestedSum} compact />}
            totalValue={<Money money={group.totalDebtPrice} compact />}
            currentLabel={isPaidTable ? 'Взыскано' : 'Собрано'}
            totalLabel={isPaidTable ? 'Прогноз взыскания' : 'Сумма сбора'}
            segments={buildGroupSegments(group)}
          />
        </DataTable.Cell>
        <DataTable.Cell>
          <button
            type="button"
            className={styles.disclosure}
            onClick={handleDisclosureClick}
            aria-expanded={expanded}
            aria-controls={membersId}
          >
            <span className={styles.disclosureLabel}>
              {expanded
                ? 'Свернуть'
                : `${group.memberCount} ${declOfNum(group.memberCount, [
                    'проект',
                    'проекта',
                    'проектов',
                  ])}`}
            </span>
            <ChevronIcon
              className={cn(styles.chevron, expanded && styles.chevronExpanded)}
              direction="down"
              size={16}
              color="currentColor"
            />
          </button>
        </DataTable.Cell>
      </DataTable.Row>

      <div className={styles.members} id={membersId} hidden={!expanded}>
        {members.map((member) => (
          <PrimaryMarketTableRow
            project={member}
            statusKey={statusKey}
            className={styles.memberRow}
            key={member.uuid}
          />
        ))}
      </div>
    </div>
  );
};

export default PrimaryMarketGroupRow;
